import { WritableDraft } from 'immer/dist/types/types-external'

import { HomeTab } from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'

import { defaultAppConfig, defaultAppSettings } from '@/constants/common'
import { defaultPalette } from '@/constants/themes'
import { BroadcastsType, CalendarType } from '@/types/servicesTypes/configTypes'
import { CommonStoreType } from '@/types/storeTypes/commonStoreTypes'
import { StateCreatorType } from '@/types/storeTypes/storeType'

const commonStore: StateCreatorType<CommonStoreType> = (set, get) => ({
  isAppHidden: false,
  currentEvent: null,
  appConfig: defaultAppConfig,
  appConfigStatus: null,
  calendar: [],
  calendarIndex: 0,
  mediaVideo: null,
  youTubeVideo: null,
  usercentricsPopup: false,
  consentStatus: null,
  listPopup: null,
  subMenuType: null,
  popUp: null,
  authPopup: null,
  paymentPopup: null,
  cataloguePages: null,
  broadcasts: null,
  allChannels: null,
  channelEvents: {},
  appSettings: defaultAppSettings,
  appPalette: defaultPalette,
  customPalette: defaultPalette,
  userCreditsWallets: [],
  isLoading: {
    calendar: false,
  },
  hasError: {
    calendar: false,
  },

  setIsAppHidden: (isAppHidden) =>
    set(
      (state) => {
        state.common.isAppHidden = isAppHidden
      },
      false,
      'setIsAppHidden',
    ),
  setCurrentEvent: (currentEvent) => {
    if (currentEvent?.id === get().common.currentEvent?.id) {
      return
    }
    set(
      (state) => {
        if (currentEvent === null) {
          state.common.currentEvent = currentEvent
          return
        }
        if (currentEvent.id === state.common.currentEvent?.id) {
          state.common.currentEvent = {
            ...state.common.currentEvent,
            ...currentEvent,
          }
          return
        }
        state.common.currentEvent = {
          ...currentEvent,
          prevEvent: state.common.currentEvent,
        }
      },
      false,
      'setCurrentEvent',
    )
  },
  resetEventHistory: () => {
    set(
      (state) => {
        if (state.common.currentEvent?.prevEvent) {
          state.common.currentEvent.prevEvent = null
        }
      },
      false,
      'resetEventHistory',
    )
  },
  openPrevEvent: () => {
    set(
      (state) => {
        state.common.currentEvent = state.common.currentEvent?.prevEvent || null
      },
      false,
      'openPrevEvent',
    )
  },
  setCalendar: (calendar: CalendarType[]) =>
    set(
      (state) => {
        state.common.calendar = calendar
      },
      false,
      'setCalendar',
    ),
  setCalendarSelectedIndex: (index) =>
    set(
      (state) => {
        state.common.calendarIndex = index
      },
      false,
      'setCalendarSelectedIndex',
    ),
  setAppConfig: (appConfig) =>
    set(
      (state) => {
        state.common.appConfig = appConfig
      },
      false,
      'setAppConfig',
    ),
  setAppConfigStatus: (appConfigStatus) =>
    set(
      (state) => {
        state.common.appConfigStatus = appConfigStatus
      },
      false,
      'setAppConfigStatus',
    ),
  setBroadcastChannelList: (broadcastId, channelList) => {
    const broadcasts = get().common.broadcasts
    const activeChannel =
      broadcasts?.[broadcastId as keyof BroadcastsType]?.activeChannel || null
    set(
      (state) => {
        state.common.broadcasts = {
          ...broadcasts,
          [broadcastId]: { activeChannel, channelList },
        }
      },
      false,
      'setBroadcastChannelList',
    )
  },
  setActiveBroadcastChannel: (broadcastId, activeChannel) => {
    const broadcasts = get().common.broadcasts
    const channelList =
      broadcasts?.[broadcastId as keyof BroadcastsType]?.channelList || []
    set(
      (state) => {
        state.common.broadcasts = {
          ...broadcasts,
          [broadcastId]: { activeChannel, channelList },
        }
      },
      false,
      'setActiveBroadcastChannel',
    )
  },
  setMediaVideo: (mediaVideo) =>
    set(
      (state) => {
        state.common.mediaVideo = mediaVideo
      },
      false,
      'setMediaVideo',
    ),
  setYouTubeVideo: (youTubeVideo) =>
    set(
      (state) => {
        state.common.youTubeVideo = youTubeVideo
      },
      false,
      'setYouTubeVideo',
    ),
  setUsercentricsPopup: (usercentricsPopup) =>
    set(
      (state) => {
        state.common.usercentricsPopup = usercentricsPopup
      },
      false,
      'setUsercentricsPopup',
    ),
  setConsentStatus: (consentStatus) =>
    set(
      (state) => {
        state.common.consentStatus = consentStatus
      },
      false,
      'setConsentStatus',
    ),
  setListPopup: (listPopup) =>
    set(
      (state) => {
        state.common.listPopup = listPopup
      },
      false,
      'setListPopup',
    ),
  setSubMenuType: (subMenuType) =>
    set(
      (state) => {
        state.common.subMenuType = subMenuType
      },
      false,
      'setSubMenuType',
    ),
  setPopUp: (popUp) =>
    set(
      (state) => {
        state.common.popUp = popUp
      },
      false,
      'setPopUp',
    ),
  setCataloguePages: (cataloguePages) =>
    set(
      (state) => {
        state.common.cataloguePages = cataloguePages
      },
      false,
      'setCataloguePages',
    ),
  setAllChannels: (channels) =>
    set(
      (state) => {
        state.common.allChannels = channels
      },
      false,
      'setAllChannels',
    ),
  setChannelEvent: (channelId, event) => {
    set(
      (state) => {
        state.common.channelEvents[channelId] = event
      },
      false,
      'setChannelEvent',
    )
  },
  setAppSettings: (settings) =>
    set(
      (state) => {
        state.common.appSettings = {
          ...state.common.appSettings,
          ...settings,
        }
      },
      false,
      'setAppSettings',
    ),
  setAppPalette: (appPalette) =>
    set(
      (state) => {
        state.common.appPalette = appPalette
      },
      false,
      'setAppPalette',
    ),
  setCustomPalette: (customPalette) =>
    set(
      (state) => {
        state.common.customPalette = customPalette
      },
      false,
      'setCustomPalette',
    ),
  setUserCreditsWallets: (userCreditsWallets) =>
    set(
      (state) => {
        state.common.userCreditsWallets = userCreditsWallets
      },
      false,
      'setUserCreditsWallets',
    ),

  setIsLoading: (loadingObj) =>
    set(
      (state) =>
        void (state.common.isLoading = {
          ...state.common.isLoading,
          ...loadingObj,
        }),
      false,
      'setIsLoading',
    ),
  setHasError: (errorObj) =>
    set(
      (state) =>
        void (state.common.hasError = {
          ...state.common.hasError,
          ...errorObj,
        }),
      false,
      'setError',
    ),
  onBoughtEvent: (eventId) =>
    set(
      (state) => {
        const updateStages = (homeTab: WritableDraft<HomeTab>) => {
          homeTab.stageLinesPage?.stage.slides.forEach((slide) => {
            if (slide.event?.id === eventId) {
              slide.event.userRights = {
                canAccess: true,
                reasons: [{ type: 'Ppv' }],
              }
            }
          })
        }
        state.common.appConfig.home?.homeTab?.forEach(updateStages)
        state.page.page?.homeTab?.forEach(updateStages)

        if (
          state.common.currentEvent &&
          state.common.currentEvent.id === eventId
        ) {
          state.common.currentEvent.userRights = {
            canAccess: true,
            reasons: [{ type: 'Ppv' }],
          }
        }

        Object.values(state.common.channelEvents).forEach((event) => {
          if (event?.id === eventId) {
            event.userRights = {
              canAccess: true,
              reasons: [{ type: 'Ppv' }],
            }
          }
        })
      },
      false,
      'onBoughtEvent',
    ),
  setAuthPopup: (authPopup) =>
    set(
      (state) => {
        state.common.authPopup = authPopup
      },
      false,
      'setAuthPopup',
    ),
  setPaymentPopup: (paymentPopup) =>
    set(
      (state) => {
        state.common.paymentPopup = paymentPopup
      },
      false,
      'setPaymentPopup',
    ),
})

export default commonStore
